import { useTranslations } from "use-intl";
import type { EnglishMessages } from "@financialharmony/i18n/messageTypes";
import { formatInterestRatePercent } from "@financialharmony/tax-se";
import {
  CARRY_OVER_CHOOSE_NOTICE,
  CARRY_OVER_DEFAULT_NOTICE,
  DRAFT_BLOCKERS,
  LATE_INTEREST_BLOCKER_PREFIX,
  NO_BANKGIRO_NOTICE_SUFFIX,
  RECEIVING_ACCOUNT_REQUIRED,
  RECEIVING_ACCOUNT_UNAVAILABLE,
} from "@financialharmony/invoicing";

/** The late-payment-interest picker's option labels and its range refusal, in the language the app shows. */
export function useInterestRateText() {
  const t = useTranslations("consultantInvoiceCreate.interest");
  return {
    optionLabel(rate: number, statutoryRatePercent: number | null): string {
      const stated = `${formatInterestRatePercent(rate)} %`;
      if (rate === statutoryRatePercent) return t("statutory", { rate: stated });
      if (rate === 0) return t("noInterest", { rate: stated });
      return stated;
    },
    problem(rate: number | null, maxRate: number | null): string | null {
      if (rate == null || maxRate == null) return null;
      if (rate < 0) return t("negative");
      if (rate > maxRate) return t("exceeds", { max: String(maxRate) });
      return null;
    },
  };
}

const BLOCKER_KEYS = new Map<string, Exclude<keyof EnglishMessages["consultantInvoiceCreate"]["blockers"], "interest">>([
  [DRAFT_BLOCKERS.sellerIncomplete, "sellerIncomplete"],
  [RECEIVING_ACCOUNT_REQUIRED, "receivingRequired"],
  [RECEIVING_ACCOUNT_UNAVAILABLE, "receivingUnavailable"],
  [DRAFT_BLOCKERS.bankAccountMissing, "bankAccountMissing"],
  [DRAFT_BLOCKERS.invoiceDateRequired, "invoiceDateRequired"],
  [DRAFT_BLOCKERS.customerRequired, "customerRequired"],
  [DRAFT_BLOCKERS.billableLineRequired, "billableLineRequired"],
  [DRAFT_BLOCKERS.customerVatNumberRequired, "customerVatNumberRequired"],
  [DRAFT_BLOCKERS.vatClassifying, "vatClassifying"],
  [DRAFT_BLOCKERS.vatClassifyFailed, "vatClassifyFailed"],
  [DRAFT_BLOCKERS.bankgiroForeignCurrency, "bankgiroForeignCurrency"],
]);

/**
 * Words the reasons `checkDraftIssueReadiness` lists in the language the app shows. The package
 * returns English sentences (the client portal reads the same ones); a reason this does not know is
 * shown as the package wrote it, never dropped.
 */
export function useDraftBlockers() {
  const t = useTranslations("consultantInvoiceCreate.blockers");
  const interest = useInterestRateText();
  return (missing: string[], interestRate: number | null, maxInterestRate: number | null): string[] =>
    missing.map((reason) => {
      const key = BLOCKER_KEYS.get(reason);
      if (key) return t(key);
      if (!reason.startsWith(LATE_INTEREST_BLOCKER_PREFIX)) return reason;
      const localized = interest.problem(interestRate, maxInterestRate);
      return t("interest", { reason: localized ?? reason.slice(LATE_INTEREST_BLOCKER_PREFIX.length) });
    });
}

/** The notice under the "Paid into" field (an account pick that dropped the bankgiro, or a copy that lost its account). */
export function useReceivingNotice() {
  const t = useTranslations("consultantInvoiceCreate.paidInto");
  return (notice: string | null): string | null => {
    if (!notice) return null;
    if (notice === CARRY_OVER_DEFAULT_NOTICE) return t("copyDefaultNotice");
    if (notice === CARRY_OVER_CHOOSE_NOTICE) return t("copyChooseNotice");
    if (notice.endsWith(NO_BANKGIRO_NOTICE_SUFFIX)) {
      return t("noBankgiroNotice", { account: notice.slice(0, -NO_BANKGIRO_NOTICE_SUFFIX.length) });
    }
    return notice;
  };
}
